import { supabase } from '../lib/supabase';

/**
 * Admin Authentication Service
 * Handles admin-specific registration, login, and verification operations
 * Uses trigger-based user_profiles creation with enhanced security
 */
export const adminAuthService = {
  /**
   * Register a new admin account
   * @param {Object} credentials - Admin registration data
   * @param {string} credentials.email - Admin email (corporate domain)
   * @param {string} credentials.password - Admin password
   * @param {string} credentials.fullName - Admin full name
   * @param {string} credentials.employeeId - Employee ID
   * @param {string} credentials.department - Department name
   * @returns {Promise<Object>} Registration result with user data
   */
  async signUp({ email, password, fullName, employeeId, department }) {
    try {
      // Validate corporate email domain (optional additional validation)
      const emailDomain = email?.split('@')?.[1];
      const allowedDomains = ['phangantaxi.com', 'admin.phangantaxi.com'];
      
      if (!allowedDomains?.includes(emailDomain)) {
        throw new Error('Please use a corporate email address');
      }

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'admin', // Trigger will use this to set role in user_profiles
            employee_id: employeeId,
            department: department
          },
          emailRedirectTo: window.location?.origin
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign in existing admin
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @returns {Promise<Object>} Sign in result
   */
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Verify user has admin role
      const { data: profile } = await supabase?.from('user_profiles')?.select('role, is_active')?.eq('id', data?.user?.id)?.single();

      if (profile?.role !== 'admin') {
        await supabase?.auth?.signOut();
        throw new Error('This account is not registered as an administrator');
      }

      if (!profile?.is_active) {
        await supabase?.auth?.signOut();
        throw new Error('This admin account has been deactivated. Contact system administrator.');
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Sign out current admin
   * @returns {Promise<Object>} Sign out result
   */
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Get current session with admin profile
   * @returns {Promise<Object|null>} Admin session with profile data or null if not authenticated
   */
  async getCurrentSession() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) return null;

      // Get admin profile
      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', session?.user?.id)
        ?.eq('role', 'admin')
        ?.single();

      if (error || !profile) return null;

      return {
        ...session?.user,
        name: profile?.full_name,
        email: profile?.email,
        role: profile?.role,
        isActive: profile?.is_active
      };
    } catch (error) {
      console.error('Error getting current session:', error);
      return null;
    }
  },

  /**
   * Get current admin profile
   * @returns {Promise<Object>} Admin profile data
   */
  async getProfile() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.eq('role', 'admin')?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Logout admin
   * @returns {Promise<void>}
   */
  logout() {
    return this.signOut();
  }
};